import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import * as regularIcons from '@fortawesome/free-regular-svg-icons';
import * as brandIcons from '@fortawesome/free-brands-svg-icons';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

// Hook compartido para cargar iconos
const useIconLoader = () => {
    const [allIcons, setAllIcons] = useState([]);

    useEffect(() => {
        const loadIcons = () => {
            const solidIconList = Object.entries(solidIcons)
                .filter(([key]) => !['fas', 'prefix'].includes(key))
                .map(([key, icon]) => ({
                    id: `fas-${key}`,
                    name: key,
                    icon,
                    prefix: 'fas'
                }));

            const regularIconList = Object.entries(regularIcons)
                .filter(([key]) => !['far', 'prefix'].includes(key))
                .map(([key, icon]) => ({
                    id: `far-${key}`,
                    name: key,
                    icon,
                    prefix: 'far'
                }));

            const brandIconList = Object.entries(brandIcons)
                .filter(([key]) => !['fab', 'prefix'].includes(key))
                .map(([key, icon]) => ({
                    id: `fab-${key}`,
                    name: key,
                    icon,
                    prefix: 'fab'
                }));

            setAllIcons([...solidIconList, ...regularIconList, ...brandIconList]);
        };

        loadIcons();
    }, []);

    return allIcons;
};

// Componente para solo mostrar el icono
export const IconDisplay = ({ value, size = 'xl', className = '' }) => {
    const allIcons = useIconLoader();
    const [icon, setIcon] = useState(null);

    useEffect(() => {
        if (value && allIcons.length > 0) {
            const foundIcon = allIcons.find(i => i.id === value);
            setIcon(foundIcon);
        }
    }, [value, allIcons]);

    if (!icon) return null;

    return (
        <FontAwesomeIcon 
            icon={icon.icon} 
            className={`text-${size} ${className}`} 
        />
    );
};

// Componente principal con selector
const IconSelector = ({ value, onChange, label, required, className }) => {
    const allIcons = useIconLoader();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIcon, setSelectedIcon] = useState(null);

    // Establecer el icono seleccionado cuando cambia el value prop
    useEffect(() => {
        if (value && allIcons.length > 0) {
            const foundIcon = allIcons.find(icon => icon.id === value);
            if (foundIcon) {
                setSelectedIcon(foundIcon);
            }
        }
    }, [value, allIcons]);

    // Si no hay onChange, mostramos solo el icono
    if (!onChange) {
        return <IconDisplay value={value} className={className ?? ''} />;
    }

    // Filtrar iconos basado en el término de búsqueda
    const filteredIcons = allIcons.filter(icon =>
        icon.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Manejar selección de icono
    const handleIconSelect = (icon) => {
        setSelectedIcon(icon);
        onChange(icon.id);
        setIsModalOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="mb-4">
            {label && (
                <label className="mb-2.5 text-black flex justify-between">
                    <span>
                        {label} {required && <span className="text-red">*</span>}
                    </span>
                </label>
            )}
            
            {/* Cuadro de visualización del icono seleccionado */}
            <div
                className="w-full rounded border-[1.5px] p-[11px] !bg-white border-stroke bg-transparent text-black outline-none transition disabled:cursor-default disabled:bg-whiter cursor-pointer"
                onClick={() => setIsModalOpen(true)}
            >
                {selectedIcon ? (
                    <FontAwesomeIcon
                        icon={selectedIcon.icon}
                        className='text-xl mr-2'
                    />
                ) : (
                    <span className="text-gray-500">Seleccionar icono</span>
                )}
            </div>

            {/* Modal de selección de iconos */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[999]">
                    <div className="bg-white rounded-lg p-5 w-11/12 max-w-4xl max-h-[80vh] flex flex-col">
                        {/* Encabezado del modal */}
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium">Seleccionar Icono</h3>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setSearchTerm('');
                                }}
                                className="text-gray-500 hover:text-gray-700 text-xl"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        {/* Barra de búsqueda */}
                        <div className="relative mb-4">
                            <FontAwesomeIcon
                                icon={faSearch}
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                            />
                            <input
                                type="text"
                                placeholder="Buscar iconos..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Lista de iconos */}
                        <div className="grid grid-cols-5 gap-3 overflow-y-auto p-2">
                            {filteredIcons.map((icon) => (
                                <div
                                    key={icon.id}
                                    onClick={() => handleIconSelect(icon)}
                                    className={`flex flex-col items-center p-3 rounded cursor-pointer transition-colors ${
                                        selectedIcon?.id === icon.id 
                                            ? 'bg-blue-50' 
                                            : 'hover:bg-gray-100'
                                    }`}
                                >
                                    <FontAwesomeIcon
                                        icon={icon.icon}
                                        className="text-xl mb-2"
                                    />
                                    <span className="text-xs text-center break-words">
                                        {icon.name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IconSelector;